import React, { useState, useMemo } from 'react'
import { Table, TableColumnType } from '@eltoro-ui/components'
import { OrderLinePerformance, CampaignPerformance } from '../types'
import { useAppContext } from '../../../Contexts/AppContext'
import { useReportHub } from '../../../Contexts/useReportHub'
import { LinkButton } from '../../../Components/LinkButton'
import './ReportHubTopPerformingTable.scss'

interface ReportHubTopPerformingTableProps {
  data: (OrderLinePerformance | CampaignPerformance)[]
  view: 'campaign' | 'orderLine'
}

type SortKey = 'ctr' | 'cost' | 'daysLeft'
type SortDirection = 'asc' | 'desc'

export const ReportHubTopPerformingTable: React.FC<ReportHubTopPerformingTableProps> = ({
  data,
  view,
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('ctr')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const { currentOrg } = useAppContext()
  const { filters } = useReportHub()

  // Handle navigation to organization page
  const handleNavigateToOrganization = (organizationId: string) => {
    if (!organizationId) return
    // Navigate to organization page in new tab
    window.open(`/orgs/${organizationId}/org-settings`, '_blank')
  }

  // Handle navigation to order line or campaign
  const handleNavigateToItem = (
    item: OrderLinePerformance | CampaignPerformance,
  ) => {
    if (!currentOrg?.id) return

    if (view === 'orderLine' && 'orderLine' in item) {
      // Navigate to order line detail page in new tab
      window.open(
        `/campaigns/dashboard/order-lines/${item.id}?org_id=${currentOrg.id}`,
        '_blank',
      )
    } else if (view === 'campaign' && 'campaign' in item) {
      // Navigate to campaign detail page in new tab
      window.open(
        `/campaigns/dashboard/${item.id}?org_id=${currentOrg.id}`,
        '_blank',
      )
    }
  }

  // Sort data based on current sort key and direction
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      let aValue: number
      let bValue: number

      switch (sortKey) {
        case 'ctr':
          aValue = a.ctr
          bValue = b.ctr
          break
        case 'cost':
          aValue = a.cost
          bValue = b.cost
          break
        case 'daysLeft':
          aValue = a.daysLeft
          bValue = b.daysLeft
          break
        default:
          return 0
      }

      if (sortDirection === 'asc') {
        return aValue - bValue
      } else {
        return bValue - aValue
      }
    })
  }, [data, sortKey, sortDirection])

  // Handle sorting from Table component
  const handleSort = (
    _: number,
    sort?: 'asc' | 'desc',
    path?: string | string[],
  ) => {
    if (!sort || !path) return

    const pathString = Array.isArray(path) ? path[0] : path
    let newSortKey: SortKey

    // Map column paths to sort keys
    switch (pathString) {
      case 'ctrFormatted':
        newSortKey = 'ctr'
        break
      case 'costFormatted':
        newSortKey = 'cost'
        break
      case 'daysLeft':
        newSortKey = 'daysLeft'
        break
      default:
        return
    }

    setSortKey(newSortKey)
    setSortDirection(sort)
  }

  // Get default sorted column index based on current sort
  const getDefaultSorted = (): [number, 'asc' | 'desc'] | undefined => {
    let columnIndex: number
    const showOrgColumn = filters.includeChildOrganizations

    switch (sortKey) {
      case 'ctr':
        columnIndex = showOrgColumn ? 2 : 1 // CTR column
        break
      case 'cost':
        columnIndex = showOrgColumn ? 3 : 2 // Cost column
        break
      case 'daysLeft':
        columnIndex = showOrgColumn ? 4 : 3 // Days Left column
        break
      default:
        return undefined
    }
    return [columnIndex, sortDirection]
  }

  // Define table columns
  const columns: TableColumnType<
    OrderLinePerformance | CampaignPerformance
  >[] = [
    {
      label: view === 'orderLine' ? 'Order Line' : 'Campaign',
      path: view === 'orderLine' ? 'orderLine' : 'campaign',
      alignment: 'left',
      width: filters.includeChildOrganizations ? '30%' : '40%',
      removeSort: true, // First column is not sortable
      RowCell: (row) => {
        const displayText =
          view === 'orderLine' && 'orderLine' in row
            ? row.orderLine
            : view === 'campaign' && 'campaign' in row
            ? row.campaign
            : ''

        return (
          <LinkButton
            text={displayText}
            onClick={() => handleNavigateToItem(row)}
            className="order-line-name break-all"
          />
        )
      },
    },
    // Conditionally add Organization column when child orgs filter is enabled
    ...(filters.includeChildOrganizations
      ? [
          {
            label: 'Organization',
            path: 'organizationName',
            alignment: 'left' as const,
            width: '25%',
            removeSort: true,
            RowCell: (row: OrderLinePerformance | CampaignPerformance) => {
              const orgName = row.organizationName || 'Unknown Organization'
              const orgId = row.organizationId

              if (!orgId) {
                return <span className="text-tint-gray-500">{orgName}</span>
              }

              return (
                <LinkButton
                  text={orgName}
                  onClick={() => handleNavigateToOrganization(orgId)}
                  className="break-all"
                />
              )
            },
          },
        ]
      : []),
    {
      label: 'CTR',
      path: 'ctrFormatted',
      alignment: 'center',
      width: filters.includeChildOrganizations ? '15%' : '20%',
      removeFilter: true,
      RowCell: (row) => (
        <span className="ctr-value font-bold">{row.ctrFormatted}</span>
      ),
    },
    {
      label: 'Spent',
      path: 'costFormatted',
      alignment: 'right',
      width: filters.includeChildOrganizations ? '15%' : '20%',
      removeFilter: true,
      RowCell: (row) => <span className="cost-value">{row.costFormatted}</span>,
    },
    {
      label: 'Days Left',
      path: 'daysLeft',
      alignment: 'center',
      width: filters.includeChildOrganizations ? '15%' : '20%',
      removeFilter: true,
      RowCell: (row) => <span className="days-left-value">{row.daysLeft}</span>,
    },
  ]

  const title =
    view === 'orderLine'
      ? 'Top Performing Order Lines'
      : 'Top Performing Campaigns'

  const description =
    view === 'orderLine'
      ? 'Table showing the top performing order lines with their click-through rates, costs, and days remaining.'
      : 'Table showing the top performing campaigns with their click-through rates, costs, and days remaining.'

  return (
    <section
      className="ReportHubTopPerformingTable bg-base border-tint-gray-300 rounded-lg border p-6"
      aria-labelledby="table-title"
    >
      <h3
        id="table-title"
        className="text-tint-gray-900 mb-4 text-lg font-semibold"
      >
        {title}
      </h3>
      <div className="sr-only" id="table-description">
        {description}
      </div>

      {/* Show message if no data */}
      {sortedData.length === 0 ? (
        <div className="text-grey-500 flex h-64 items-center justify-center">
          No performance data available yet.
        </div>
      ) : (
        <div className="overflow-hidden">
          <Table
            rows={sortedData}
            columns={columns}
            className="w-full"
            stickyHeader={false}
            onSort={handleSort}
            defaultSorted={getDefaultSorted()}
            aria-describedby="table-description"
          />
        </div>
      )}
    </section>
  )
}
